import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';

import { getPolylineOptions } from '@/utils';
import { COLORS } from 'theme';

/**
 * Draws a vehicle route polyline onto the current Google Map instance. Avoids using directions service to minimize unnecessary API calls.
 */
const RoutePolyline = ({
  path,
  strokeColor = COLORS.kinetikBlue,
  strokeWeight = 4,
  lineStyle,
  mode,
}) => {
  const map = useMap();
  const mapsLibrary = useMapsLibrary('maps');
  const polylineRef = useRef(null);

  // Create polyline instance when map is available, and cleanup on unmount
  useEffect(() => {
    if (!map || !mapsLibrary || !window.google) return undefined;

    polylineRef.current = new window.google.maps.Polyline({
      path,
      strokeColor,
      strokeWeight,
      clickable: false,
      geodesic: true,
      map,
      ...getPolylineOptions({
        lineStyle,
        mode,
      }),
    });

    return () => {
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
    };
  }, [map, mapsLibrary]);

  // Update polyline when props change
  useEffect(() => {
    if (!polylineRef.current) return;

    polylineRef.current.setOptions({
      path,
      strokeColor,
      strokeWeight,
    });
  }, [path, strokeColor, strokeWeight]);

  return null;
};

RoutePolyline.propTypes = {
  path: PropTypes.arrayOf(
    PropTypes.shape({
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
    }).isRequired,
  ).isRequired,
  strokeColor: PropTypes.string,
  strokeWeight: PropTypes.number,
  lineStyle: PropTypes.string,
  mode: PropTypes.string,
};

export default RoutePolyline;
